import React, { useEffect, useState, useMemo, useCallback } from "react";
import { PanelGroup } from "react-resizable-panels";
import EvaluationEditor from "./EvaluationEditor";
import ProblemSubmissionTemplates from "./ProblemSubmissionTemplates";
import useMetadata from "../../../hooks/useMetadata";
import StyledPanelResizeHandle from "components/StyledPanelResizeHandle/StyledPanelResizeHandle";

const EvaluationAndSubmissionPlayground = ({
  problemData,
  setProblemData,
  isUpdate,
}) => {
  const { metadata, loading: metadataLoading } = useMetadata();
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [evalCode, setEvalCode] = useState("");
  const [editorLanguage, setEditorLanguage] = useState("");

  const activeEvaluationTemplates = useMemo(
    () =>
      problemData?.problemEvaluationTemplates?.filter((t) => t.active) || [],
    [problemData]
  );

  const activeSubmissionTemplates = useMemo(
    () =>
      problemData?.problemSubmissionTemplates?.filter((t) => t.active) || [],
    [problemData]
  );

  useEffect(() => {
    if (metadataLoading || !metadata?.programmingLanguages) {
      return;
    }

    if (selectedLanguage) {
      return;
    }

    let langToSet = null;

    if (
      isUpdate &&
      (activeEvaluationTemplates.length > 0 ||
        activeSubmissionTemplates.length > 0)
    ) {
      const firstTemplate =
        activeEvaluationTemplates[0] || activeSubmissionTemplates[0];
      if (firstTemplate) {
        langToSet = metadata.programmingLanguages.find(
          (lang) => lang.key === firstTemplate.programmingLanguage
        );
      }
    }

    if (!langToSet) {
      langToSet = metadata.programmingLanguages[0];
    }

    if (langToSet) {
      setSelectedLanguage(langToSet);
      setEditorLanguage(langToSet.key.toLowerCase());

      const evalTemplate = activeEvaluationTemplates.find(
        (t) => t.programmingLanguage === langToSet.key
      );
      setEvalCode(evalTemplate?.template || "");
    }
  }, [
    metadata,
    metadataLoading,
    activeEvaluationTemplates,
    activeSubmissionTemplates,
    selectedLanguage,
    isUpdate,
  ]);

  const handleLanguageChange = useCallback(
    (lang) => {
      if (lang) {
        setSelectedLanguage(lang);
        const evalTemplate = activeEvaluationTemplates.find(
          (t) => t.programmingLanguage === lang.key
        );
        setEvalCode(evalTemplate?.template || "");
        setEditorLanguage(lang.key.toLowerCase());
      }
    },
    [activeEvaluationTemplates]
  );

  const handleEvalCodeChange = useCallback(
    (newEvalCode) => {
      setEvalCode(newEvalCode);
      if (selectedLanguage) {
        setProblemData((prev) => {
          const templates = [...prev.problemEvaluationTemplates];
          const exists = templates.some(
            (t) => t.programmingLanguage === selectedLanguage.key && t.active
          );
          if (exists) {
            return {
              ...prev,
              problemEvaluationTemplates: templates.map((t) =>
                t.programmingLanguage === selectedLanguage.key && t.active
                  ? { ...t, template: newEvalCode }
                  : t
              ),
            };
          } else {
            return {
              ...prev,
              problemEvaluationTemplates: [
                ...templates,
                {
                  template: newEvalCode,
                  programmingLanguage: selectedLanguage.key,
                  description: null,
                  active: true,
                },
              ],
            };
          }
        });
      }
    },
    [selectedLanguage, setProblemData]
  );

  return (
    <PanelGroup
      direction="horizontal"
      style={{
        width: "100%",
        height: "100vh",
        background: "#f1f1f1",
      }}
    >
      <EvaluationEditor
        problemData={problemData}
        selectedLanguage={selectedLanguage}
        evalCode={evalCode}
        setEvalCode={handleEvalCodeChange}
        editorLanguage={editorLanguage}
        setProblemData={setProblemData}
      />
      <StyledPanelResizeHandle direction="vertical" />
      <ProblemSubmissionTemplates
        problemData={problemData}
        selectedLanguage={selectedLanguage}
        setSelectedLanguage={handleLanguageChange}
        editorLanguage={editorLanguage}
        setProblemData={setProblemData}
      />
    </PanelGroup>
  );
};

export default EvaluationAndSubmissionPlayground;
